import { posthog } from 'posthog-js';

import { getEnvVar } from '@/env';
import { trackEvent } from '@/utils/analytics';

import { GOOGLE_INTEGRATION_PROVIDER, PROVIDERS } from './providers';

export type GoogleSignupToolkit = 'gmail' | 'googlecalendar';
export interface GoogleSignupConsentConfig {
  toolkits: GoogleSignupToolkit[];
  scopes: string[];
  flow?: 'consent-during-sign-in' | 'consent-after-sign-in' | null;
}

type GoogleConsentFlow = NonNullable<GoogleSignupConsentConfig['flow']>;

export const GOOGLE_SIGNUP_VISITOR_ID_KEY = 'google-signup-visitor-id';
const EMPTY_CONSENT_CONFIG: GoogleSignupConsentConfig = {
  toolkits: [],
  scopes: [],
  flow: null,
};

export async function fetchGoogleSignupConsentConfig(): Promise<GoogleSignupConsentConfig> {
  // Keep the experiment assignment when PostHog identifies the user after sign-in
  const visitorId: unknown =
    sessionStorage.getItem(GOOGLE_SIGNUP_VISITOR_ID_KEY) ??
    posthog.get_distinct_id();
  if (typeof visitorId !== 'string' || !visitorId) return EMPTY_CONSENT_CONFIG;
  try {
    const url = new URL(
      `${getEnvVar('VITE_API_URL')}/google-signup/consent-config`
    );
    url.searchParams.set('visitor_id', visitorId);
    const response = await fetch(url, { signal: AbortSignal.timeout(2500) });
    return response.ok
      ? ((await response.json()) as GoogleSignupConsentConfig)
      : EMPTY_CONSENT_CONFIG;
  } catch {
    return EMPTY_CONSENT_CONFIG;
  }
}

export async function prepareGoogleSignupConsent(
  params: URLSearchParams,
  connection: string
): Promise<GoogleSignupToolkit[]> {
  if (connection !== PROVIDERS.google.connection) return [];
  const visitorId: unknown = posthog.get_distinct_id();
  sessionStorage.removeItem(GOOGLE_SIGNUP_VISITOR_ID_KEY);
  if (typeof visitorId === 'string' && visitorId)
    sessionStorage.setItem(GOOGLE_SIGNUP_VISITOR_ID_KEY, visitorId);
  const consentConfig = await fetchGoogleSignupConsentConfig();
  if (!consentConfig.flow || consentConfig.toolkits.length === 0) return [];
  params.set('connection', GOOGLE_INTEGRATION_PROVIDER.connection);
  params.set('max_age', '0');
  const identityScopes = ['openid', 'email', 'profile'];
  params.set('connection_scope', identityScopes.join(','));
  if (consentConfig.flow !== 'consent-during-sign-in') return [];
  params.set(
    'connection_scope',
    [...identityScopes, ...consentConfig.scopes].join(',')
  );
  params.set('access_type', 'offline');
  params.set('prompt', 'consent');
  // Sent at once, because the sign-in redirect can unload the page
  trackEvent(
    'GOOGLE_CONSENT_OFFERED',
    {
      flow: consentConfig.flow,
      surface: 'sign_in',
      toolkits: consentConfig.toolkits,
    },
    true
  );
  return consentConfig.toolkits;
}

export function trackGoogleConsentResult(result: {
  flow: GoogleConsentFlow;
  surface: 'sign_in' | 'onboarding';
  outcome: 'connected' | 'nothing_connected' | 'abandoned' | 'failed';
  requestedToolkits: GoogleSignupToolkit[];
  connectedToolkits: GoogleSignupToolkit[];
}): void {
  trackEvent('GOOGLE_CONSENT_RESULT', {
    flow: result.flow,
    surface: result.surface,
    outcome: result.outcome,
    requested_toolkits: result.requestedToolkits,
    connected_toolkits: result.connectedToolkits,
  });
}

export async function recordGoogleSignupIntent(
  accessToken: string,
  toolkits: GoogleSignupToolkit[]
): Promise<void> {
  if (toolkits.length === 0) return;
  try {
    const response = await fetch(
      `${getEnvVar('VITE_API_URL')}/google-signup/intent`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toolkits,
          visitor_id: sessionStorage.getItem(GOOGLE_SIGNUP_VISITOR_ID_KEY),
        }),
      }
    );
    if (response.ok) return;
    console.error('Could not record Google sign-up consent:', response.status);
  } catch (error) {
    // Consent import failures leave the onboarding connection controls available
    console.error('Could not record Google sign-up consent:', error);
  }
  // The backend reports every outcome after the intent, so only this one is sent here
  trackGoogleConsentResult({
    flow: 'consent-during-sign-in',
    surface: 'sign_in',
    outcome: 'failed',
    requestedToolkits: toolkits,
    connectedToolkits: [],
  });
}
